import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Mail, Zap, AlertCircle, UserPlus, LogOut, RefreshCw } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(165, 145, 239, 0.5);
  animation: slideUp 0.3s ease;
  border: 1px solid ${props => props.theme.border};

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
    color: ${props => props.theme.text};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const DeviceInfo = styled.div`
  background: ${props => props.theme.background};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  border: 1px solid ${props => props.theme.border};
`;

const DeviceInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DeviceInfoLabel = styled.span`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  font-weight: 500;
`;

const DeviceInfoValue = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text};
  font-weight: 600;
`;

const OptionSection = styled.div`
  margin-bottom: 24px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 16px;
  background: ${props => props.theme.background};
  border-radius: 12px;
  border: 2px solid ${props => props.checked ? props.theme.primary : props.theme.border};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.border};
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: ${props => props.theme.primary};
  margin-top: 2px;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OptionDescription = styled.div`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  line-height: 1.5;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.theme.border};
  border-radius: 8px;
  margin-bottom: 24px;
  border-left: 3px solid ${props => props.theme.primary};
`;

const InfoText = styled.p`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const ModeSelector = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const ModeCard = styled.button`
  flex: 1;
  padding: 20px 16px;
  background: ${props => props.$selected ? props.theme.primary + '20' : props.theme.background};
  border: 2px solid ${props => props.$selected ? props.theme.primary : props.theme.border};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.$selected ? props.theme.primary + '20' : props.theme.border};
  }
`;

const ModeIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$color || props.theme.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModeTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const ModeDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  text-align: center;
  line-height: 1.4;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid ${props => props.theme.border};
`;

const Button = styled.button`
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${props => props.theme.border};
  color: ${props => props.theme.text};

  &:hover:not(:disabled) {
    background: ${props => props.theme.textSecondary};
  }
`;

const LaunchButton = styled(Button)`
  background: var(--error);
  color: white;

  &:hover:not(:disabled) {
    background: #d33426;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(234, 67, 53, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const GmailModal = ({ device, onClose, onLaunch, onDisconnect, onUpdateMainAccount, theme }) => {
  const [mode, setMode] = useState('create'); // 'create', 'disconnect' ou 'update_main'
  const [withRestarter, setWithRestarter] = useState(false);
  const [closeAppsAfter, setCloseAppsAfter] = useState(true);
  const [isLaunching, setIsLaunching] = useState(false);

  const handleLaunch = async () => {
    setIsLaunching(true);
    try {
      if (mode === 'create') {
        await onLaunch(device.device_id, withRestarter, closeAppsAfter);
      } else if (mode === 'disconnect') {
        await onDisconnect(device.device_id, device.device_name);
      } else if (mode === 'update_main') {
        await onUpdateMainAccount(device.device_id, device.device_name);
      }
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>
            <Mail size={24} color="var(--error)" />
            Actions Gmail
          </ModalTitle>
          <CloseButton theme={theme} onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <DeviceInfo theme={theme}>
            <DeviceInfoRow>
              <DeviceInfoLabel theme={theme}>Device</DeviceInfoLabel>
              <DeviceInfoValue theme={theme}>{device.device_name || device.device_id}</DeviceInfoValue>
            </DeviceInfoRow>
            <DeviceInfoRow>
              <DeviceInfoLabel theme={theme}>ID</DeviceInfoLabel>
              <DeviceInfoValue theme={theme}>{device.device_id}</DeviceInfoValue>
            </DeviceInfoRow>
            {device.gmail_account && (
              <DeviceInfoRow>
                <DeviceInfoLabel theme={theme}>Compte principal</DeviceInfoLabel>
                <DeviceInfoValue theme={theme}>{device.gmail_account}</DeviceInfoValue>
              </DeviceInfoRow>
            )}
          </DeviceInfo>

          {/* Sélecteur de mode */}
          <ModeSelector>
            <ModeCard 
              theme={theme} 
              $selected={mode === 'create'}
              onClick={() => setMode('create')}
            >
              <ModeIcon theme={theme} $color="#34A853">
                <UserPlus size={24} color="#34A853" />
              </ModeIcon>
              <ModeTitle theme={theme}>Créer un compte</ModeTitle>
              <ModeDescription theme={theme}>
                Créer un nouveau compte Gmail
              </ModeDescription>
            </ModeCard>

            <ModeCard 
              theme={theme} 
              $selected={mode === 'update_main'}
              onClick={() => setMode('update_main')}
            >
              <ModeIcon theme={theme} $color="#4285F4">
                <RefreshCw size={24} color="#4285F4" />
              </ModeIcon>
              <ModeTitle theme={theme}>Changer principal</ModeTitle>
              <ModeDescription theme={theme}>
                Détecter et mettre à jour le compte principal
              </ModeDescription>
            </ModeCard>

            <ModeCard 
              theme={theme} 
              $selected={mode === 'disconnect'}
              onClick={() => setMode('disconnect')}
            >
              <ModeIcon theme={theme} $color="var(--error)">
                <LogOut size={24} color="var(--error)" />
              </ModeIcon>
              <ModeTitle theme={theme}>Déconnecter</ModeTitle>
              <ModeDescription theme={theme}>
                Déconnecter tous sauf le principal
              </ModeDescription>
            </ModeCard>
          </ModeSelector>

          {mode === 'create' ? (
            <>
              <InfoBox theme={theme}>
                <AlertCircle size={16} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
                <InfoText theme={theme}>
                  Le script va créer automatiquement un nouveau compte Gmail sur ce device.
                </InfoText>
              </InfoBox>

              <OptionSection>
                <OptionLabel 
                  theme={theme} 
                  checked={withRestarter}
                >
                  <Checkbox
                    type="checkbox"
                    checked={withRestarter}
                    onChange={(e) => setWithRestarter(e.target.checked)}
                    theme={theme}
                  />
                  <OptionContent>
                    <OptionTitle theme={theme}>
                      <Zap size={16} color={theme.primary} />
                      Lancer le restarteur avant
                    </OptionTitle>
                    <OptionDescription theme={theme}>
                      Redémarre la connexion du device via le restarteur associé au workflow avant de lancer le script Gmail.
                    </OptionDescription>
                  </OptionContent>
                </OptionLabel>
                
                <OptionLabel 
                  theme={theme} 
                  checked={closeAppsAfter}
                >
                  <Checkbox
                    type="checkbox"
                    checked={closeAppsAfter}
                    onChange={(e) => setCloseAppsAfter(e.target.checked)}
                    theme={theme}
                  />
                  <OptionContent>
                    <OptionTitle theme={theme}>
                      <X size={16} color={theme.primary} />
                      Fermer les applications après
                    </OptionTitle>
                    <OptionDescription theme={theme}>
                      Ferme automatiquement toutes les applications récentes après la création du compte.
                    </OptionDescription>
                  </OptionContent>
                </OptionLabel>
              </OptionSection>
            </>
          ) : mode === 'update_main' ? (
            <InfoBox theme={theme}>
              <AlertCircle size={16} color="#4285F4" style={{ marginTop: '2px', flexShrink: 0 }} />
              <InfoText theme={theme}>
                Le script va ouvrir Gmail, cliquer sur l'icône de profil, scanner tous les comptes présents et déterminer le compte principal (celui en haut de la liste). 
                Le champ <strong>gmail_account</strong> de la table devices sera mis à jour avec ce compte.
              </InfoText>
            </InfoBox>
          ) : (
            <InfoBox theme={theme}>
              <AlertCircle size={16} color="var(--error)" style={{ marginTop: '2px', flexShrink: 0 }} />
              <InfoText theme={theme}>
                Le script va déconnecter tous les comptes Gmail de ce device <strong>sauf le compte principal</strong> ({device.gmail_account || 'non défini'}).
                Les comptes déconnectés seront enregistrés dans la base de données.
              </InfoText>
            </InfoBox>
          )}
        </ModalBody>

        <ModalFooter theme={theme}>
          <CancelButton theme={theme} onClick={onClose} disabled={isLaunching}>
            Annuler
          </CancelButton>
          <LaunchButton 
            onClick={handleLaunch} 
            disabled={isLaunching}
            style={{ 
              background: mode === 'disconnect' ? 'var(--error)' : mode === 'update_main' ? '#4285F4' : '#34A853' 
            }}
          >
            {mode === 'create' ? <UserPlus size={18} /> : mode === 'update_main' ? <RefreshCw size={18} /> : <LogOut size={18} />}
            {isLaunching ? 'Lancement...' : (mode === 'create' ? 'Créer un compte' : mode === 'update_main' ? 'Détecter le compte principal' : 'Déconnecter')}
          </LaunchButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default GmailModal;
